import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/BackButton';
import api from '../../services/api';
import './ChatDashboard.css';

const ChatDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadProfiles();
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadProfiles = async () => {
    try {
      const response = await api.get('/api/chat/profile/visible');
      setProfiles(response.data);
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await api.get('/api/chat/messages/unread-count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadProfiles();
      return;
    }
    try {
      const response = await api.get(`/api/chat/profile/search?q=${encodeURIComponent(searchTerm)}`);
      setProfiles(response.data);
    } catch (error) {
      console.error('Error searching profiles:', error);
    }
  };

  const startChat = (username) => {
    navigate(`/chat/direct/${username}`);
  };

  return (
    <div className="chat-dashboard">
      <BackButton />
      <div className="chat-container">
        <div className="chat-sidebar">
          <h2>Chat</h2>
          <div className="chat-actions">
            <Link to="/chat/rooms" className="btn-chat-action">
              💬 Chat Rooms {user?.roles?.includes('ROLE_PREMIUM') && '(Premium)'}
            </Link>
            <Link to="/chat/my-profile" className="btn-chat-action">
              👤 My Profile
            </Link>
          </div>
          
          <div className="search-section">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="search-input"
            />
            <button onClick={handleSearch} className="btn-search">Search</button>
          </div>

          <div className="users-list">
            <h3>Available Users {unreadCount > 0 && <span className="badge-unread">{unreadCount}</span>}</h3>
            {loading ? (
              <div className="loading">Loading users...</div>
            ) : profiles.length === 0 ? (
              <div className="no-users">No users found</div>
            ) : (
              profiles.map(profile => (
                <div
                  key={profile.username}
                  className="user-item"
                  onClick={() => startChat(profile.username)}
                >
                  <div className="user-avatar">
                    {profile.avatarUrl ? (
                      <img src={profile.avatarUrl} alt={profile.displayName || profile.username} />
                    ) : (
                      <div className="avatar-placeholder">
                        {profile.displayName?.charAt(0) || profile.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="user-info">
                    <div className="user-name">{profile.displayName || profile.username}</div>
                    <div className="user-status">
                      {profile.lastSeen ? `Last seen: ${new Date(profile.lastSeen).toLocaleString()}` : 'Online'}
                    </div>
                  </div>
                  {profile.isPremium && <span className="badge-premium">⭐</span>}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="chat-main">
          <div className="welcome-message">
            <h1>Welcome to Chat!</h1>
            <p>Select a user from the sidebar to start chatting</p>
            <p>Or create a chatroom if you're a premium member</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatDashboard;

